/**
 * THE GUIDE LINES a PTA/PTB channel casts: its midline, and the excursion bounds beyond it.
 *
 * <pre>
 *   high + 3h  · · · · · · · · · ·   DOTTED   two band-heights past the flanking band
 *   high + 2h  ───────────────────   solid    one band-height past it
 *   high + h   ┌─────────────────┐            the upper projection ends here
 *   high       ├─────────────────┤   TOP      the measured edge
 *              ┈┈┈┈┈┈┈┈┈┈┈┈┈┈┈┈┈┈   EQUATOR   exactly midway
 *   low        ├─────────────────┤   BOTTOM
 *   low  - h   └─────────────────┘            the lower projection ends here
 *   low  - 2h  ───────────────────   solid
 *   low  - 3h  · · · · · · · · · ·   DOTTED
 * </pre>
 *
 * <h2>One module, because there are two renderers</h2>
 *
 * The replay draws this structure as a `markLine` and the cycle presets draw it as their own
 * series, reached by different buttons and labelled identically on screen. A guide declared in
 * one of them is a guide the other does not have, and the two disagreeing is invisible until
 * somebody puts a replay and a cycle run on the same chart — which this file has already paid
 * for twice, with the projected range bands and with PTB's colour.
 *
 * <h2>Multiples of the band's OWN height, never a fixed distance</h2>
 *
 * A channel is a measured range, so the distance a reader expects price to travel beyond it is
 * in units of that range. A fixed price offset would be most of a 1993 channel and a rounding
 * error against a 2026 one, which is the same argument that put the structural tolerance in ATRs
 * rather than in percent.
 */

/** How many band-heights beyond the channel's edge each bound sits. */
export const GUIDE_SOLID_MULTIPLE = 2;
export const GUIDE_DOTTED_MULTIPLE = 3;

/**
 * THIN, because a guide is not a measurement.
 *
 * <p>The channel's own top and bottom carry {@link CHANNEL_BORDER_WIDTH}; these are derived from
 * them, and the weight is what says which is which — the same rule that gives the projected
 * range bands no stroke at all.
 */
export const GUIDE_LINE_WIDTH = 1;

/**
 * The guides for one channel, nearest first.
 *
 * <p>Nothing at all for a band with one edge or no height: a midline needs two levels to sit
 * between, and a zero-height channel would stack nine lines on one price.
 *
 * @param {number} top the measured high
 * @param {number} bottom the measured low
 * @returns {Array<{level:number, kind:string, dash:string}>} `kind` is 'equator', 'solid' or
 *          'dotted'; `dash` is the ECharts line type to draw it with
 */
export function guideLevels(top, bottom) {
  if (!Number.isFinite(top) || !Number.isFinite(bottom)) return [];
  const high = Math.max(top, bottom);
  const low = Math.min(top, bottom);
  const h = high - low;
  if (!(h > 0)) return [];
  return [
    // The midline first: it is the only guide INSIDE the channel, and the one a reader looks for.
    { level: (high + low) / 2, kind: 'equator', dash: 'dotted' },
    { level: high + GUIDE_SOLID_MULTIPLE * h, kind: 'solid', dash: 'solid' },
    { level: low - GUIDE_SOLID_MULTIPLE * h, kind: 'solid', dash: 'solid' },
    { level: high + GUIDE_DOTTED_MULTIPLE * h, kind: 'dotted', dash: 'dotted' },
    { level: low - GUIDE_DOTTED_MULTIPLE * h, kind: 'dotted', dash: 'dotted' },
  ];
}
